import type moment from "moment";
import type { z } from "zod";

import instance from "..";
import { looseArray, looseParse } from "../looseParse";
import {
  highYieldCashAccruedInterestSummarySchema,
  highYieldCashAprTierSchema,
  highYieldCashStatusResponseSchema,
  type highYieldCashStatusSchema,
} from "./hyc.schema";

export type HighYieldCashStatus = z.infer<typeof highYieldCashStatusSchema>;

export type HighYieldCashAprTier = z.infer<typeof highYieldCashAprTierSchema>;

export type HighYieldCashStatusResponse = z.infer<
  typeof highYieldCashStatusResponseSchema
>;

export type HighYieldCashAccruedInterestSummary = z.infer<
  typeof highYieldCashAccruedInterestSummarySchema
>;

export const getHighYieldCashStatus = async (
  accountID: string,
): Promise<HighYieldCashStatusResponse> => {
  const res = await instance.get<HighYieldCashStatusResponse>(
    `/accounts/${accountID}/high_yield_cash/status`,
  );

  return looseParse(
    highYieldCashStatusResponseSchema,
    res.data,
    "getHighYieldCashStatus",
  );
};

export const getHighYieldCashAprTier = async (
  accountID: string,
): Promise<HighYieldCashAprTier> => {
  const res = await instance.get<HighYieldCashAprTier>(
    `/accounts/${accountID}/high_yield_cash/apr_tier`,
  );

  return looseParse(
    highYieldCashAprTierSchema,
    res.data,
    "getHighYieldCashAprTier",
  );
};

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Copy the backend typo key onto the correct field before looseParse/looseArray.
 *
 * On a failed row parse, looseArray retains the raw object and Zod transforms
 * never run — remapping here is what keeps `monthly_accrued_interest` readable
 * for those retained rows. The schema transform still covers the success path.
 * Can be deleted when https://github.com/alpacahq/gobroker/pull/8911 ships.
 */
const remediateAccruedInterestTypo = (row: unknown): unknown => {
  if (!isPlainRecord(row)) {
    return row;
  }
  const typo = row.montly_accrued_interest;
  if (typo == null || row.monthly_accrued_interest != null) {
    return row;
  }
  return { ...row, monthly_accrued_interest: typo };
};

export const getHighYieldCashAccruedInterest = async (
  accountID: string,
  dates: moment.Moment[],
): Promise<HighYieldCashAccruedInterestSummary[]> => {
  const datesParam = dates.map((d) => d.format("YYYY-MM-DD")).join(",");
  const res = await instance.get<HighYieldCashAccruedInterestSummary[]>(
    `/accounts/${accountID}/high_yield_cash/accrued_interest_summary?dates=${datesParam}`,
  );

  // Remapping must run before parse: retained malformed rows skip the schema
  // transform (see remediateAccruedInterestTypo).
  const remediated = (Array.isArray(res.data) ? res.data : []).map(
    remediateAccruedInterestTypo,
  );

  return looseParse(
    looseArray(
      highYieldCashAccruedInterestSummarySchema,
      "getHighYieldCashAccruedInterest",
    ),
    remediated,
    "getHighYieldCashAccruedInterest",
  );
};
